"use client";

import Link from "next/link";
import { useState } from "react";
import { m, AnimatePresence } from "motion/react";
import { Radar, ArrowRight } from "lucide-react";
import BlueprintBackground from "@/components/BlueprintBackground";
import MetadataUpdater from "@/components/MetadataUpdater";
import { useTranslation } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/translations";

// --- Types ---

type Ring = "adopt" | "trial" | "assess" | "hold";
type Quadrant = "ai-ml" | "frontend-mobile" | "backend-devops" | "cloud-infra";

interface TechEntry {
  id: string;
  nameKey: TranslationKey;
  descKey: TranslationKey;
  ring: Ring;
  quadrant: Quadrant;
  isNew?: boolean;
  x: number; // SVG x coordinate (center at 400)
  y: number; // SVG y coordinate (center at 400)
}

// --- Data ---

const ringRadii = [100, 200, 300, 380]; // adopt, trial, assess, hold boundaries

const ringMeta: { id: Ring; labelKey: TranslationKey; descKey: TranslationKey; color: string; svgFill: string }[] = [
  { id: "adopt", labelKey: "radar.ring.adopt", descKey: "radar.ring.adopt.desc", color: "bg-mint", svgFill: "#70D993" },
  { id: "trial", labelKey: "radar.ring.trial", descKey: "radar.ring.trial.desc", color: "bg-mint-text", svgFill: "#1A8A4A" },
  { id: "assess", labelKey: "radar.ring.assess", descKey: "radar.ring.assess.desc", color: "bg-amber-400", svgFill: "#FBBF24" },
  { id: "hold", labelKey: "radar.ring.hold", descKey: "radar.ring.hold.desc", color: "bg-text-faint", svgFill: "#9CA3AF" },
];

const quadrantMeta: { id: Quadrant; labelKey: TranslationKey }[] = [
  { id: "ai-ml", labelKey: "radar.quadrant.ai" },
  { id: "frontend-mobile", labelKey: "radar.quadrant.frontend" },
  { id: "backend-devops", labelKey: "radar.quadrant.backend" },
  { id: "cloud-infra", labelKey: "radar.quadrant.cloud" },
];

// Technologies with hand-tuned positions in SVG space (viewBox 0 0 800 800, center 400 400)
// Quadrant layout: top-right = AI & ML, bottom-right = Frontend & Mobile,
//                  bottom-left = Backend & DevOps, top-left = Cloud & Infra
const technologies: TechEntry[] = [
  // AI & ML (top-right quadrant)
  { id: "langchain", nameKey: "radar.tech.langchain", descKey: "radar.tech.langchain.desc", ring: "adopt", quadrant: "ai-ml", isNew: true, x: 450, y: 350 },
  { id: "n8n", nameKey: "radar.tech.n8n", descKey: "radar.tech.n8n.desc", ring: "adopt", quadrant: "ai-ml", x: 480, y: 370 },
  { id: "rag", nameKey: "radar.tech.rag", descKey: "radar.tech.rag.desc", ring: "adopt", quadrant: "ai-ml", isNew: true, x: 430, y: 320 },
  { id: "langgraph", nameKey: "radar.tech.langgraph", descKey: "radar.tech.langgraph.desc", ring: "trial", quadrant: "ai-ml", isNew: true, x: 520, y: 360 },
  { id: "ollama", nameKey: "radar.tech.ollama", descKey: "radar.tech.ollama.desc", ring: "trial", quadrant: "ai-ml", isNew: true, x: 480, y: 260 },
  { id: "llamacpp", nameKey: "radar.tech.llamacpp", descKey: "radar.tech.llamacpp.desc", ring: "trial", quadrant: "ai-ml", isNew: true, x: 510, y: 300 },
  { id: "fastmcp", nameKey: "radar.tech.fastmcp", descKey: "radar.tech.fastmcp.desc", ring: "trial", quadrant: "ai-ml", isNew: true, x: 530, y: 280 },
  { id: "edge-ai", nameKey: "radar.tech.edge-ai", descKey: "radar.tech.edge-ai.desc", ring: "assess", quadrant: "ai-ml", isNew: true, x: 570, y: 200 },

  // Frontend & Mobile (bottom-right quadrant)
  { id: "react", nameKey: "radar.tech.react", descKey: "radar.tech.react.desc", ring: "adopt", quadrant: "frontend-mobile", x: 480, y: 425 },
  { id: "tailwindcss", nameKey: "radar.tech.tailwindcss", descKey: "radar.tech.tailwindcss.desc", ring: "adopt", quadrant: "frontend-mobile", x: 420, y: 480 },
  { id: "figma", nameKey: "radar.tech.figma", descKey: "radar.tech.figma.desc", ring: "adopt", quadrant: "frontend-mobile", x: 460, y: 400 },
  { id: "nextjs", nameKey: "radar.tech.nextjs", descKey: "radar.tech.nextjs.desc", ring: "adopt", quadrant: "frontend-mobile", x: 450, y: 440 },
  { id: "flutter", nameKey: "radar.tech.flutter", descKey: "radar.tech.flutter.desc", ring: "adopt", quadrant: "frontend-mobile", x: 430, y: 455 },
  { id: "typescript", nameKey: "radar.tech.typescript", descKey: "radar.tech.typescript.desc", ring: "adopt", quadrant: "frontend-mobile", x: 470, y: 470 },
  { id: "deno", nameKey: "radar.tech.deno", descKey: "radar.tech.deno.desc", ring: "assess", quadrant: "frontend-mobile", x: 580, y: 560 },
  { id: "bun", nameKey: "radar.tech.bun", descKey: "radar.tech.bun.desc", ring: "assess", quadrant: "frontend-mobile", isNew: true, x: 520, y: 600 },
  { id: "wasm", nameKey: "radar.tech.wasm", descKey: "radar.tech.wasm.desc", ring: "assess", quadrant: "frontend-mobile", isNew: true, x: 600, y: 500 },
  { id: "jquery", nameKey: "radar.tech.jquery", descKey: "radar.tech.jquery.desc", ring: "hold", quadrant: "frontend-mobile", x: 580, y: 700 },
  { id: "angular", nameKey: "radar.tech.angular", descKey: "radar.tech.angular.desc", ring: "hold", quadrant: "frontend-mobile", x: 620, y: 650 },

  // Backend & DevOps (bottom-left quadrant)
  { id: "nodejs", nameKey: "radar.tech.nodejs", descKey: "radar.tech.nodejs.desc", ring: "adopt", quadrant: "backend-devops", x: 360, y: 405 },
  { id: "nestjs", nameKey: "radar.tech.nestjs", descKey: "radar.tech.nestjs.desc", ring: "adopt", quadrant: "backend-devops", x: 380, y: 420 },
  { id: "mysql", nameKey: "radar.tech.mysql", descKey: "radar.tech.mysql.desc", ring: "hold", quadrant: "backend-devops", x: 100, y: 600 },
  { id: "mongodb", nameKey: "radar.tech.mongodb", descKey: "radar.tech.mongodb.desc", ring: "adopt", quadrant: "backend-devops", x: 350, y: 455 },
  { id: "nodered", nameKey: "radar.tech.nodered", descKey: "radar.tech.nodered.desc", ring: "trial", quadrant: "backend-devops", x: 220, y: 435 },
  { id: "docker", nameKey: "radar.tech.docker", descKey: "radar.tech.docker.desc", ring: "adopt", quadrant: "backend-devops", x: 385, y: 440 },
  { id: "postgresql", nameKey: "radar.tech.postgresql", descKey: "radar.tech.postgresql.desc", ring: "adopt", quadrant: "backend-devops", x: 330, y: 470 },
  { id: "python", nameKey: "radar.tech.python", descKey: "radar.tech.python.desc", ring: "adopt", quadrant: "backend-devops", x: 300, y: 410 },
  { id: "supabase", nameKey: "radar.tech.supabase", descKey: "radar.tech.supabase.desc", ring: "trial", quadrant: "backend-devops", x: 305, y: 530 },
  { id: "github-actions", nameKey: "radar.tech.github-actions", descKey: "radar.tech.github-actions.desc", ring: "adopt", quadrant: "backend-devops", x: 370, y: 495 },
  { id: "traefik", nameKey: "radar.tech.traefik", descKey: "radar.tech.traefik.desc", ring: "adopt", quadrant: "backend-devops", x: 310, y: 425 },
  { id: "rust", nameKey: "radar.tech.rust", descKey: "radar.tech.rust.desc", ring: "assess", quadrant: "backend-devops", x: 200, y: 600 },
  { id: "django", nameKey: "radar.tech.django", descKey: "radar.tech.django.desc", ring: "adopt", quadrant: "backend-devops", x: 325, y: 440 },
  { id: "php", nameKey: "radar.tech.php", descKey: "radar.tech.php.desc", ring: "hold", quadrant: "backend-devops", x: 190, y: 700 },
  { id: "java", nameKey: "radar.tech.java", descKey: "radar.tech.java.desc", ring: "hold", quadrant: "backend-devops", x: 145, y: 650 },

  // Cloud & Infra (top-left quadrant)
  { id: "aws", nameKey: "radar.tech.aws", descKey: "radar.tech.aws.desc", ring: "trial", quadrant: "cloud-infra", x: 380, y: 280 },
  { id: "authentik", nameKey: "radar.tech.authentik", descKey: "radar.tech.authentik.desc", ring: "adopt", quadrant: "cloud-infra", x: 320, y: 385 },
  { id: "oidc", nameKey: "radar.tech.oidc", descKey: "radar.tech.oidc.desc", ring: "trial", quadrant: "cloud-infra", x: 320, y: 320 },
  { id: "cloudflare", nameKey: "radar.tech.cloudflare", descKey: "radar.tech.cloudflare.desc", ring: "adopt", quadrant: "cloud-infra", x: 350, y: 355 },
  { id: "terraform", nameKey: "radar.tech.terraform", descKey: "radar.tech.terraform.desc", ring: "adopt", quadrant: "cloud-infra", x: 340, y: 370 },
  { id: "azure", nameKey: "radar.tech.azure", descKey: "radar.tech.azure.desc", ring: "trial", quadrant: "cloud-infra", x: 260, y: 350 },
  { id: "ansible", nameKey: "radar.tech.ansible", descKey: "radar.tech.ansible.desc", ring: "trial", quadrant: "cloud-infra", x: 350, y: 300 },
  { id: "platformio", nameKey: "radar.tech.platformio", descKey: "radar.tech.platformio.desc", ring: "trial", quadrant: "cloud-infra", x: 300, y: 240 },
  { id: "arduino", nameKey: "radar.tech.arduino", descKey: "radar.tech.arduino.desc", ring: "trial", quadrant: "cloud-infra", x: 340, y: 220 },
  { id: "proxmox", nameKey: "radar.tech.proxmox", descKey: "radar.tech.proxmox.desc", ring: "trial", quadrant: "cloud-infra", x: 300, y: 280 },
  { id: "mqtt", nameKey: "radar.tech.mqtt", descKey: "radar.tech.mqtt.desc", ring: "trial", quadrant: "cloud-infra", x: 250, y: 320 },
  { id: "gcp", nameKey: "radar.tech.gcp", descKey: "radar.tech.gcp.desc", ring: "trial", quadrant: "cloud-infra", x: 280, y: 370 },
  { id: "firebase", nameKey: "radar.tech.firebase", descKey: "radar.tech.firebase.desc", ring: "trial", quadrant: "cloud-infra", x: 310, y: 260 },
];

function getRingColor(ring: Ring): string {
  return ringMeta.find((r) => r.id === ring)?.svgFill ?? "#9CA3AF";
}

// --- Component ---

export default function TechRadarPage() {
  const { t } = useTranslation();
  const [selectedTech, setSelectedTech] = useState<string | null>(null);

  const selectedEntry = technologies.find((tech) => tech.id === selectedTech);

  return (
    <>
    <MetadataUpdater
      titleKey="meta.techRadar.title"
      descriptionKey="meta.techRadar.description"
    />
    <BlueprintBackground />
    <main className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-mint-text/30 text-mint-text text-[10px] font-mono tracking-[0.3em] uppercase mb-6">
            <Radar size={14} />
            {t("radar.label")}
          </div>
          <h1 className="text-4xl md:text-5xl font-heading text-text-primary mb-4">
            {t("radar.title")}
          </h1>
          <p className="text-text-muted max-w-2xl mx-auto mb-3 leading-relaxed">
            {t("radar.description")}
          </p>
          <p className="text-[10px] font-mono text-text-faint tracking-widest uppercase">
            {t("radar.updated")}
          </p>
        </m.div>

        {/* Desktop: SVG Radar */}
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="hidden md:block mb-8"
        >
          <svg
            viewBox="0 0 800 800"
            className="w-full max-w-2xl mx-auto"
            role="img"
            aria-label="Technology Radar"
          >
            {/* Ring circles */}
            {ringRadii.map((r, i) => (
              <circle
                key={r}
                cx={400}
                cy={400}
                r={r}
                fill="none"
                stroke="var(--border-faint)"
                strokeWidth={i === 0 ? "1.5" : "1"}
                opacity={0.6}
              />
            ))}

            {/* Quadrant divider lines */}
            <line
              x1={400}
              y1={20}
              x2={400}
              y2={780}
              stroke="var(--border-faint)"
              strokeWidth="1"
              opacity={0.4}
            />
            <line
              x1={20}
              y1={400}
              x2={780}
              y2={400}
              stroke="var(--border-faint)"
              strokeWidth="1"
              opacity={0.4}
            />

            {/* Ring labels along the right axis */}
            {[
              { label: "ADOPT", y: 400 - 50 },
              { label: "TRIAL", y: 400 - 150 },
              { label: "ASSESS", y: 400 - 250 },
              { label: "HOLD", y: 400 - 340 },
            ].map((item) => (
              <text
                key={item.label}
                x={405}
                y={item.y}
                className="fill-text-faint"
                fontSize="8"
                fontFamily="var(--font-mono)"
                letterSpacing="0.15em"
                opacity={0.4}
              >
                {item.label}
              </text>
            ))}

            {/* Quadrant labels */}
            <text
              x={600}
              y={55}
              textAnchor="middle"
              className="fill-mint-text"
              fontSize="10"
              fontFamily="var(--font-mono)"
              letterSpacing="0.2em"
              fontWeight="bold"
            >
              {t("radar.quadrant.ai")}
            </text>
            <text
              x={600}
              y={760}
              textAnchor="middle"
              className="fill-mint-text"
              fontSize="10"
              fontFamily="var(--font-mono)"
              letterSpacing="0.2em"
              fontWeight="bold"
            >
              {t("radar.quadrant.frontend")}
            </text>
            <text
              x={200}
              y={760}
              textAnchor="middle"
              className="fill-mint-text"
              fontSize="10"
              fontFamily="var(--font-mono)"
              letterSpacing="0.2em"
              fontWeight="bold"
            >
              {t("radar.quadrant.backend")}
            </text>
            <text
              x={200}
              y={55}
              textAnchor="middle"
              className="fill-mint-text"
              fontSize="10"
              fontFamily="var(--font-mono)"
              letterSpacing="0.2em"
              fontWeight="bold"
            >
              {t("radar.quadrant.cloud")}
            </text>

            {/* Technology dots */}
            {technologies.map((tech, i) => {
              const isSelected = selectedTech === tech.id;
              const color = getRingColor(tech.ring);
              return (
                <g
                  key={tech.id}
                  className="cursor-pointer"
                  onClick={() =>
                    setSelectedTech(
                      selectedTech === tech.id ? null : tech.id
                    )
                  }
                >
                  {/* Hover/selection highlight ring */}
                  <circle
                    cx={tech.x}
                    cy={tech.y}
                    r={isSelected ? 14 : 10}
                    fill="transparent"
                    className="hover:fill-mint/10 transition-all"
                  />
                  {/* Main dot */}
                  <m.circle
                    cx={tech.x}
                    cy={tech.y}
                    r={isSelected ? 8 : 6}
                    fill={color}
                    initial={{ opacity: 0, r: 0 }}
                    animate={{
                      opacity: isSelected ? 1 : 0.8,
                      r: isSelected ? 8 : 6,
                    }}
                    transition={{ duration: 0.3, delay: i * 0.03 }}
                    stroke={isSelected ? "var(--text-primary)" : "transparent"}
                    strokeWidth={isSelected ? 2 : 0}
                  />
                  {/* "NEW" badge */}
                  {tech.isNew && (
                    <circle cx={tech.x + 9} cy={tech.y - 9} r={3} fill="#EF4444" />
                  )}
                  {/* Label */}
                  <text
                    x={tech.x + 14}
                    y={tech.y + 3}
                    className="fill-text-faint"
                    fontSize="8"
                    fontFamily="var(--font-mono)"
                    letterSpacing="0.1em"
                  >
                    {t(tech.nameKey)}
                  </text>
                </g>
              );
            })}
          </svg>
        </m.div>

        {/* Mobile: List view */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="md:hidden space-y-8 mb-8"
        >
          {quadrantMeta.map((quadrant) => (
            <div key={quadrant.id}>
              <h3 className="text-[10px] font-mono text-mint-text tracking-[0.3em] uppercase mb-4 border-b border-border-faint pb-2">
                {t(quadrant.labelKey)}
              </h3>
              {(["adopt", "trial", "assess", "hold"] as Ring[]).map((ring) => {
                const techs = technologies.filter(
                  (tech) => tech.quadrant === quadrant.id && tech.ring === ring
                );
                if (techs.length === 0) return null;
                const ringInfo = ringMeta.find((r) => r.id === ring)!;
                return (
                  <div key={ring} className="mb-4">
                    <div className="text-[9px] font-mono text-text-faint tracking-widest uppercase mb-2">
                      {t(ringInfo.labelKey)}
                    </div>
                    {techs.map((tech) => (
                      <button
                        key={tech.id}
                        onClick={() =>
                          setSelectedTech(
                            selectedTech === tech.id ? null : tech.id
                          )
                        }
                        className={`w-full text-left p-3 border mb-2 transition-all ${
                          selectedTech === tech.id
                            ? "border-mint-text/30 bg-bg-card"
                            : "border-border-faint bg-bg-card hover:border-mint-text/20"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2.5 h-2.5 rounded-full shrink-0`}
                            style={{ backgroundColor: ringInfo.svgFill }}
                          />
                          <span className="text-sm font-bold text-text-primary uppercase tracking-wide">
                            {t(tech.nameKey)}
                          </span>
                          {tech.isNew && (
                            <span className="text-[8px] font-mono text-red-500 tracking-widest uppercase">
                              NEW
                            </span>
                          )}
                        </div>
                        <AnimatePresence>
                          {selectedTech === tech.id && (
                            <m.p
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="mt-2 text-xs text-text-muted leading-relaxed overflow-hidden"
                            >
                              {t(tech.descKey)}
                            </m.p>
                          )}
                        </AnimatePresence>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </m.div>

        {/* Detail panel (desktop — below SVG) */}
        <AnimatePresence>
          {selectedEntry && (
            <m.div
              key={selectedEntry.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="hidden md:block p-6 bg-bg-card border border-border-faint mb-8 max-w-2xl mx-auto"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: getRingColor(selectedEntry.ring) }}
                />
                <h3 className="font-bold text-text-primary uppercase tracking-wide">
                  {t(selectedEntry.nameKey)}
                </h3>
                <span className="text-[10px] font-mono text-mint-text tracking-widest uppercase">
                  {t(
                    ringMeta.find((r) => r.id === selectedEntry.ring)!
                      .labelKey
                  )}
                </span>
                {selectedEntry.isNew && (
                  <span className="text-[8px] font-mono text-red-500 tracking-widest uppercase border border-red-500/30 px-1.5 py-0.5">
                    NEW
                  </span>
                )}
              </div>
              <p className="text-sm text-text-muted leading-relaxed">
                {t(selectedEntry.descKey)}
              </p>
            </m.div>
          )}
        </AnimatePresence>

        {/* Legend */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12"
        >
          {ringMeta.map((ring) => (
            <div
              key={ring.id}
              className="p-4 border border-border-faint bg-bg-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: ring.svgFill }}
                />
                <span className="text-xs font-bold text-text-primary uppercase tracking-wide">
                  {t(ring.labelKey)}
                </span>
              </div>
              <p className="text-[10px] text-text-faint leading-relaxed">
                {t(ring.descKey)}
              </p>
            </div>
          ))}
        </m.div>

        {/* CTA */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-mint p-10 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8"
        >
          <div>
            <h2 className="text-2xl sm:text-3xl font-heading font-black text-black uppercase tracking-tighter mb-2">
              {t("radar.cta.title")}
            </h2>
            <p className="text-black/70 font-bold">
              {t("radar.cta.desc")}
            </p>
          </div>
          <Link
            href="/#contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-black tracking-widest text-xs uppercase hover:bg-black/80 transition-colors shrink-0"
          >
            {t("radar.cta.button")}
            <ArrowRight size={16} />
          </Link>
        </m.div>
      </div>
    </main>
    </>
  );
}
